import {
  Box,
  Button,
  Field,
  HStack,
  IconButton,
  Stack,
  Status,
  Table,
  Text,
} from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { LuPencil, LuPlus, LuX } from 'react-icons/lu'
import { Tooltip } from '@/components/ui/tooltip'
import { formatCapacity, formatIsoDateDE, type PlannedValue } from '@/helper/valueChange.helper'

export type ServiceCapacityTableRow = {
  /** Stable key, the service code. */
  key: string
  label: string
  /** The compact unit description shown under the name. */
  helper: string
  required: boolean
  /** The capacity in effect; null when nothing is set yet. */
  activeValue: number | null
  /** Note under the value, e.g. "(seit 01.01.2026)". */
  activeNote?: string | null
  /** Changes scheduled for a later date, earliest first. */
  planned: PlannedValue[]
  /** The planned change currently loaded into the editor, if any. */
  editingId: number | null
  /** Whether this row's inline editor is open; `editor` is rendered below the row then. */
  editorOpen: boolean
  editor: ReactNode
  /** Left out for a year that takes no more changes: the row then offers no actions. */
  onPlan?: () => void
  onEditPlanned?: (planned: PlannedValue) => void
  onDropPlanned?: (planned: PlannedValue) => void
}

type ServiceCapacityTableProps = {
  rows: ServiceCapacityTableRow[]
  /** The running year; a change dated into a later year says so. */
  visibleYear?: number | null
  disabled?: boolean
}

function yearOf(iso: string): number {
  return Number(iso.slice(0, 4))
}

/**
 * The capacity section of "Auftragsvolumen eintragen": one row per service with the volume in
 * effect, its planned change and the actions on it (SIB-1119). Values and dates are shown, never
 * typed, until the user plans or edits a change - the editor the page passes in then opens under
 * the row. Structure only: the page owns the drafts, validation and the save.
 */
export function ServiceCapacityTable({
  rows,
  visibleYear = null,
  disabled = false,
}: ServiceCapacityTableProps) {
  return (
    <Box overflowX="auto">
      <Table.Root size="sm" minW="640px" data-testid="service-capacity-table">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Leistung</Table.ColumnHeader>
            <Table.ColumnHeader>Aktuelles Volumen</Table.ColumnHeader>
            <Table.ColumnHeader>Geplante Änderung</Table.ColumnHeader>
            <Table.ColumnHeader>
              <Text as="span" srOnly>
                Aktionen
              </Text>
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map(row => (
            <ServiceCapacityRow
              key={row.key}
              row={row}
              visibleYear={visibleYear}
              disabled={disabled}
            />
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}

function ServiceCapacityRow({
  row,
  visibleYear,
  disabled,
}: {
  row: ServiceCapacityTableRow
  visibleYear: number | null
  disabled: boolean
}) {
  const { label } = row
  const canAct = !!(row.onPlan || row.onEditPlanned || row.onDropPlanned)

  return (
    <>
      <Table.Row data-testid={`capacity-service-${row.key}`}>
        <Table.Cell>
          <Field.Root required={row.required}>
            <Field.Label>
              {label} {row.required ? <Field.RequiredIndicator /> : null}
            </Field.Label>
            <Field.HelperText>{row.helper}</Field.HelperText>
          </Field.Root>
        </Table.Cell>

        <Table.Cell>
          {row.activeValue === null ? (
            <Text color="fg.muted" data-testid={`active-value-${label}`}>
              Noch kein Wert hinterlegt
            </Text>
          ) : (
            <Stack gap={0}>
              <Text as="span" data-testid={`active-value-${label}`}>
                {formatCapacity(row.activeValue)}
              </Text>
              {row.activeNote && <Text color="fg.muted">{row.activeNote}</Text>}
            </Stack>
          )}
        </Table.Cell>

        <Table.Cell>
          {row.planned.length === 0 ? (
            <Text color="fg.muted">Keine geplante Änderung</Text>
          ) : (
            <Stack gap={2}>
              {row.planned.map(change => {
                const laterYear =
                  visibleYear !== null && yearOf(change.validFrom) > visibleYear
                    ? yearOf(change.validFrom)
                    : null
                return (
                  <Stack key={change.id} gap={0} data-testid={`planned-change-${row.key}`}>
                    <Status.Root colorPalette="blue">
                      <Status.Indicator />
                      Geplant
                    </Status.Root>
                    <Text as="span" data-testid={`planned-value-${label}`}>
                      {formatCapacity(change.value)} ab {formatIsoDateDE(change.validFrom)}
                      {row.editingId === change.id && (
                        <>
                          {' '}
                          <Text as="span" color="fg.muted">
                            (wird bearbeitet)
                          </Text>
                        </>
                      )}
                    </Text>
                    {laterYear !== null && (
                      <Text color="fg.muted">Gilt im Planungsjahr {laterYear}.</Text>
                    )}
                  </Stack>
                )
              })}
            </Stack>
          )}
        </Table.Cell>

        <Table.Cell>
          {canAct && (
            <HStack gap={2} flexWrap="wrap" justify="flex-end">
              {row.onPlan && row.planned.length === 0 && !row.editorOpen && (
                <Button variant="plain" onClick={row.onPlan} disabled={disabled}>
                  <LuPlus />
                  {row.activeValue === null ? 'Volumen festlegen' : 'Volumenänderung planen'}
                </Button>
              )}
              {row.planned.map(change => (
                <HStack key={change.id} gap={2}>
                  {row.onEditPlanned && (
                    <Tooltip content="Bearbeiten">
                      <IconButton
                        variant="outline"
                        aria-label="Bearbeiten"
                        onClick={() => row.onEditPlanned?.(change)}
                        disabled={disabled || row.editingId === change.id}
                      >
                        <LuPencil />
                      </IconButton>
                    </Tooltip>
                  )}
                  {row.onDropPlanned && (
                    <Tooltip content="Geplante Änderung verwerfen">
                      <IconButton
                        variant="outline"
                        colorPalette="red"
                        aria-label="Geplante Änderung verwerfen"
                        onClick={() => row.onDropPlanned?.(change)}
                        disabled={disabled}
                      >
                        <LuX />
                      </IconButton>
                    </Tooltip>
                  )}
                </HStack>
              ))}
            </HStack>
          )}
        </Table.Cell>
      </Table.Row>

      {row.editorOpen && (
        <Table.Row data-testid={`capacity-editor-${row.key}`}>
          <Table.Cell colSpan={4}>
            <Box role="group" aria-label={`Volumenänderung ${label}`}>
              {row.editor}
            </Box>
          </Table.Cell>
        </Table.Row>
      )}
    </>
  )
}
